import RouterService from '@ember/routing/router-service';
import Transition from '@ember/routing/-private/transition';
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import SegmentModel from 'cio/models/segment';
import SqlImportModel from 'cio/models/sql-import';
import SqlImportDatabaseModel from 'cio/models/sql-import-database';
import FormValidations from 'cio/utils/form-validations';
import SqlImportsCreateSettings from 'cio/components/sql-imports/create/settings';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import { assertExists } from 'cio/utils/assertions';
import DS from 'ember-data';

export default class SqlImportsImportSettingsController extends Controller {
  declare model: { sqlImport: SqlImportModel; databases: SqlImportDatabaseModel[] };

  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service router!: RouterService;
  @service store!: DS.Store;

  @tracked settingsValidations?: FormValidations;
  @tracked databases: SqlImportDatabaseModel[] = [];
  @tracked showQueryReviewModal = false;
  @tracked showSegmentAndIntervalWarningModal = false;
  @tracked resetIntervalCounts = false;
  @tracked transition?: Transition;
  @tracked private originalDatabaseId?: SqlImportDatabaseModel['id'];
  @tracked private originalSegmentId?: SegmentModel['id'];
  @tracked private originalImportType?: SqlImportModel['importType'];
  @tracked private originalInterval?: SqlImportModel['interval'];

  setDefaults() {
    this.databases = this.model.databases.toArray();
    this.originalDatabaseId = this.model.sqlImport.belongsTo('database').id();
    this.originalSegmentId = this.model.sqlImport.segment.get('id');
    this.originalInterval = this.model.sqlImport.get('interval');
    this.originalImportType = this.model.sqlImport.get('importType');
    this.transition = undefined;
  }

  @action
  async resetModel() {
    this.model.sqlImport.rollbackAttributes();
    this.database?.rollbackAttributes();
    this.resetIntervalCounts = true;

    if (this.hasChangedDatabase) {
      assertExists(this.originalDatabaseId);
      this.model.sqlImport.set('database', this.store.peekRecord('sql-import-database', this.originalDatabaseId));
    }

    if (this.hasChangedSegment) {
      assertExists(this.originalSegmentId);

      let realSegment =
        this.store.peekRecord('segment', this.originalSegmentId) ??
        (await this.store.findRecord('segment', this.originalSegmentId));
      this.model.sqlImport.set('segment', realSegment);
    }
  }

  get database() {
    return this.model.sqlImport.belongsTo('database').value() as SqlImportDatabaseModel | null;
  }

  get hasChangedDatabase() {
    return this.database?.id !== this.originalDatabaseId;
  }

  get hasChangedSegment() {
    return this.model.sqlImport.segment.get('id') !== this.originalSegmentId;
  }

  get hasChangedImportType() {
    return this.model.sqlImport.importType !== this.originalImportType;
  }

  get hasChangedInterval() {
    return this.model.sqlImport.get('interval') !== this.originalInterval;
  }

  get hasChangedIdFieldName() {
    return 'idFieldName' in this.model.sqlImport.changedAttributes();
  }

  @action
  didInsertSettings(context: SqlImportsCreateSettings) {
    this.settingsValidations = context.validations;
  }

  @action
  onCreateDatabase(database: SqlImportDatabaseModel) {
    this.databases = [...this.databases, database];
  }

  @action
  onDeleteDatabase(database: SqlImportDatabaseModel) {
    this.databases = this.databases.filter((d) => d.id !== database.id);
  }

  @action
  goToQuery() {
    this.showQueryReviewModal = false;
    this.router.transitionTo('sql-imports.import.query', {
      queryParams: {
        runQuery: true,
      },
    });
  }

  @action
  confirmChanges(transition: Transition) {
    this.showSegmentAndIntervalWarningModal = true;
    this.transition = transition;
  }

  @action
  discardChanges() {
    this.model.sqlImport.set('interval', this.originalInterval);
    if (this.originalSegmentId) {
      assertExists(this.originalSegmentId);
      this.model.sqlImport.set('segment', this.store.peekRecord('segment', this.originalSegmentId));
    }
    this.model.sqlImport.set('importType', this.originalImportType);
    this.showSegmentAndIntervalWarningModal = false;
    if (this.hasChangedDatabase) {
      assertExists(this.originalDatabaseId);
      this.model.sqlImport.set('database', this.store.peekRecord('sql-import-database', this.originalDatabaseId));
    }
    this.transition?.retry();
  }

  @action
  async saveSegmentAndIntervalChanges() {
    this.showSegmentAndIntervalWarningModal = false;
    let { transition } = this;
    await this.saveTask.perform();
    transition?.retry();
  }

  @action
  async confirmQueryChange() {
    await this.saveTask.perform();
    this.showQueryReviewModal = true;
  }

  saveTask = task(async () => {
    try {
      await this.settingsValidations?.validateModel();
    } catch {
      return;
    }

    try {
      let hasChangedIdFieldName = 'idFieldName' in this.model.sqlImport.changedAttributes();
      await this.model.sqlImport.save();
      // showing of confirmation modals is handled by the route, unless user is saving directly.
      if ((hasChangedIdFieldName || this.hasChangedDatabase) && !this.transition) {
        this.showQueryReviewModal = true;
      } else {
        this.alerts.success('sql-import.settings', 'Settings updated');
      }

      this.setDefaults();
    } catch (err) {
      this.apiErrors.report(err);
    }
  });
}

declare module '@ember/controller' {
  interface Registry {
    'sql-imports.import.settings': SqlImportsImportSettingsController;
  }
}
